import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, ChevronLeft, Loader2, MessageCircle, Search, Trash2, X } from "lucide-react";
import { PageContentLoader } from "@/components/PageContentLoader";
import { useClientConfig } from "@/contexts/ClientConfigContext";
import { filterNomeInput, isNomeValido } from "@/lib/nomePessoa";
import { toast } from "@/lib/appToast";
import { cardapioApi } from "@/lib/cardapio/api";
import {
  getAtendimentoLabels,
  formatAtendimentoAmbos,
  normalizeAtendimentoNome,
  type ModoAtendimento,
} from "@/lib/atendimentoMode";
import {
  buildIdentificacaoGarcom,
  filterNumeroGarcom,
  formatIdentificacaoGarcomDisplay,
  limiteNumeroGarcom,
  normalizeNumeroGarcom,
  parseIdentificacaoGarcom,
} from "@/lib/garcom/identificacao";
import {
  listWaiterSolicitacoes,
  type CardapioWaiterPayload,
  type CardapioMesaItemNotificationItem,
  type CardapioMesaItemsPayload,
} from "@/lib/cardapioOperationalNotifications";
import {
  confirmarGarcomChamado,
  listarGarcomChamados,
  type GarcomChamado,
  type GarcomCloseAccountNotification,
  type GarcomMesaItemsNotification,
  type GarcomWaiterChamado,
} from "@/lib/garcom/chamadosApi";
import { cartItemKey, type CardapioCatalogo, type CardapioProduto, type CardapioPromocao, type CartItem } from "@/lib/cardapio/types";
import { OBSERVACAO_ITEM_MAX_LEN, normalizeItemObservacao } from "@caixa/types";
import {
  createComanda,
  findComandaAbertaPorIdentificacao,
  lookupComandasPorIdentificacao,
  getComanda,
  listComandas,
  updateComanda,
  type ComandaAberta,
} from "@caixa/lib/comandasApi";
import { formatBRL } from "@/lib/formatBrl";
import { normalizeSearchText } from "@/lib/searchText";
import { labelQuantidadeCaixa, unidadeVendaPermiteDecimal } from "@/lib/unidadeVenda";

type GarcomView = "mesas" | "menu" | "cart";

type ModalProduct = {
  produto_id: number;
  nome: string;
  preco_unitario: number;
  max_quantidade: number;
  unidade_venda: string;
  imagem_url?: string | null;
};

type MesaCardRow = {
  key: string;
  identificacao: string;
  mesaNumero: string | null;
  comanda: ComandaAberta | null;
  chamado: GarcomWaiterChamado | null;
  itensNotificacao: GarcomMesaItemsNotification | null;
  contaNotificacao: GarcomCloseAccountNotification | null;
};

type SelectedMesa = MesaCardRow;

type GarcomCartOrigem = "existente" | "novo";

type GarcomCartItem = CartItem & {
  origem: GarcomCartOrigem;
};

const POLL_MS = 3000;

function roundQty(n: number, permiteDecimal: boolean): number {
  if (permiteDecimal) return Math.round(n * 100) / 100;
  return Math.round(n);
}

function formatGarcomBRL(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  if (n === 0) return "R$ 00,00";
  return formatBRL(n);
}

function formatGarcomQuantidadeNotificacao(value: number): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n - Math.round(n)) < 0.0001) return String(Math.round(n));
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

function getMesaItemsNotificacao(payload: CardapioMesaItemsPayload): CardapioMesaItemNotificationItem[] {
  return Array.isArray(payload.itens)
    ? payload.itens.filter((item) => Number(item.quantidade || 0) > 0 && Boolean(item.nome_produto))
    : [];
}

function atendimentoNumeroFromNome(nome: string, modo: ModoAtendimento): string | null {
  const numero = parseIdentificacaoGarcom(nome, modo).numero;
  return numero || null;
}

function normalizeIdentificacao(value: string | null | undefined): string {
  return (value ?? "").trim();
}

function normalizeIdentificacaoSearch(value: string | null | undefined): string {
  return normalizeIdentificacao(value).toLocaleLowerCase("pt-BR");
}

function isCardapioComanda(comanda: ComandaAberta | null | undefined): boolean {
  if (!comanda) return false;
  return (
    comanda.origem === "cardapio" ||
    Boolean(comanda.cliente_nome?.trim()) ||
    Boolean(comanda.cliente_whatsapp?.trim())
  );
}

function mesaKeyFromNumero(numero: string, modo: ModoAtendimento): string {
  return `atendimento:${modo}:${numero}`;
}

function formatGarcomAtendimentoDisplay(
  modo: ModoAtendimento,
  identificacaoRaw: string | null | undefined,
  clienteNomeRaw?: string | null,
  fallbackId?: number,
): string {
  const identificacao = normalizeIdentificacao(identificacaoRaw);
  const clienteNome = normalizeIdentificacao(clienteNomeRaw);
  const parsed = parseIdentificacaoGarcom(identificacao, modo);
  if (modo === "ambos") return formatAtendimentoAmbos(clienteNome || parsed.nome, parsed.numero, parsed.mesa);

  if (parsed.numero) {
    return formatIdentificacaoGarcomDisplay(modo, clienteNome || parsed.nome, parsed.numero);
  }

  return clienteNome || identificacao || (fallbackId != null ? String(fallbackId) : getAtendimentoLabels(modo).singular);
}

function garcomComandaDisplay(comanda: ComandaAberta, modo: ModoAtendimento): string {
  return formatGarcomAtendimentoDisplay(modo, comanda.nome, comanda.cliente_nome, comanda.id);
}

function garcomComandaRowKey(comanda: ComandaAberta, modo: ModoAtendimento): string {
  if (modo === "ambos") return `comanda:${comanda.id}`;
  const numero = atendimentoNumeroFromNome(comanda.nome, modo);
  if (isCardapioComanda(comanda) && numero != null) {
    return mesaKeyFromNumero(numero, modo);
  }
  return `comanda:${comanda.id}`;
}

function notificacaoComandaId(chamado: GarcomChamado): number | null {
  if (chamado.tipo !== "mesa_items" && chamado.tipo !== "close_account" && chamado.tipo !== "waiter_request") return null;
  const id = Number((chamado.payload as { comanda_id?: number }).comanda_id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function garcomChamadoFallbackKey(chamado: GarcomChamado, modo: ModoAtendimento): string | null {
  const numero = atendimentoNumeroFromNome(chamado.mesa, modo);
  if (numero != null) return mesaKeyFromNumero(numero, modo);
  const identificacao = normalizeIdentificacao(chamado.mesa);
  return identificacao ? `ident:${identificacao}` : null;
}

function garcomChamadoRowKey(
  chamado: GarcomChamado,
  comandaKeyById: Map<number, string>,
  modo: ModoAtendimento,
): string | null {
  const comandaId = notificacaoComandaId(chamado);
  if (comandaId != null) {
    return comandaKeyById.get(comandaId) ?? null;
  }
  return garcomChamadoFallbackKey(chamado, modo);
}

function garcomChamadoDisplay(chamado: GarcomChamado, modo: ModoAtendimento): string {
  return formatGarcomAtendimentoDisplay(modo, chamado.mesa, chamado.cliente_nome);
}

function chamadoMatchesMesaRow(chamado: GarcomChamado, row: MesaCardRow, modo: ModoAtendimento): boolean {
  const comandaId = notificacaoComandaId(chamado);
  if (comandaId != null) return row.comanda?.id === comandaId;

  const numero = atendimentoNumeroFromNome(chamado.mesa, modo);
  if (numero != null) return row.key === mesaKeyFromNumero(numero, modo);

  const identificacao = normalizeIdentificacao(chamado.mesa);
  return identificacao !== "" && identificacao === row.identificacao;
}

function findCatalogProduct(catalogo: CardapioCatalogo, produtoId: number): CardapioProduto | null {
  for (const cat of catalogo.categorias) {
    const found = cat.produtos.find((p) => p.id === produtoId);
    if (found) return found;
  }
  return null;
}

function garcomCartRowKey(origem: GarcomCartOrigem, produtoId: number, observacao: string): string {
  return `${origem}:${cartItemKey(produtoId, observacao)}`;
}

function garcomCartSignature(items: GarcomCartItem[]): string {
  return [...items]
    .map(
      (i) =>
        `${i.origem}:${i.produto_id}:${normalizeItemObservacao(i.observacao)}:${Number(i.quantidade).toFixed(3)}`,
    )
    .sort()
    .join("|");
}

function remainingProductQuantity(items: GarcomCartItem[], produtoId: number, max: number): number {
  const selected = items.reduce((sum, item) => sum + (item.produto_id === produtoId ? item.quantidade : 0), 0);
  return Math.max(0, max - selected);
}

function appendNovoItem(items: GarcomCartItem[], item: GarcomCartItem, quantidade: number): GarcomCartItem[] {
  if (quantidade > remainingProductQuantity(items, item.produto_id, item.max_quantidade) + 0.0001) return items;
  const key = garcomCartRowKey("novo", item.produto_id, item.observacao);
  const existing = items.find((row) => row.key === key);
  if (existing) {
    return items.map((row) => row.key === key
      ? { ...row, quantidade: roundQty(row.quantidade + quantidade, unidadeVendaPermiteDecimal(row.unidade_venda)) }
      : row);
  }
  return [...items, { ...item, key, origem: "novo", quantidade }];
}

function ProductQtyControl({
  value,
  min = 1,
  max,
  onChange,
  permiteDecimal = false,
}: {
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
  permiteDecimal?: boolean;
}) {
  const step = permiteDecimal ? 0.01 : 1;
  const showDecrease = value > min;
  const showIncrease = max == null || value < max - 0.0001;
  const display = permiteDecimal ? value.toFixed(2) : String(value);

  const bump = (delta: number) => {
    let next = roundQty(value + delta, permiteDecimal);
    if (max != null) next = Math.min(max, next);
    if (next < min) next = min;
    onChange(next);
  };

  return (
    <div className={`cardapio-qty-control${showDecrease ? "" : " cardapio-qty-control--no-decrease"}`}>
      {showDecrease ? (
        <button type="button" onClick={() => bump(-step)} aria-label="Diminuir">
          −
        </button>
      ) : null}
      <span>{display}</span>
      {showIncrease ? <button
        type="button"
        onClick={() => bump(step)}
        aria-label="Aumentar"
      >
        +
      </button> : null}
    </div>
  );
}

export default function GarcomPage() {
  const { config: clientConfig } = useClientConfig();
  const atendimentoLabels = useMemo(
    () => getAtendimentoLabels(clientConfig.modo_atendimento),
    [clientConfig.modo_atendimento],
  );
  const [view, setView] = useState<GarcomView>("mesas");
  const [comandas, setComandas] = useState<ComandaAberta[]>([]);
  const [chamados, setChamados] = useState<GarcomChamado[]>([]);
  const [mesasLoading, setMesasLoading] = useState(true);
  const mesasRequestRef = useRef<AbortController | null>(null);
  const [mesaSearch, setMesaSearch] = useState("");
  const [selectedMesa, setSelectedMesa] = useState<SelectedMesa | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const [pedidoIdentificacao, setPedidoIdentificacao] = useState<string | null>(null);
  const [editingComandaId, setEditingComandaId] = useState<number | null>(null);
  const [catalogo, setCatalogo] = useState<CardapioCatalogo | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [produtoSearch, setProdutoSearch] = useState("");
  const [cart, setCart] = useState<GarcomCartItem[]>([]);
  const [editBaselineSignature, setEditBaselineSignature] = useState<string | null>(null);
  const [editKdsPendingItemKeys, setEditKdsPendingItemKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [modalItem, setModalItem] = useState<ModalProduct | null>(null);
  const [modalQty, setModalQty] = useState(1);
  const [modalObs, setModalObs] = useState("");
  const [identOpen, setIdentOpen] = useState(false);
  const [identNome, setIdentNome] = useState("");
  const [identNumero, setIdentNumero] = useState("");
  const [identMesa, setIdentMesa] = useState("");
  const [identCandidatas, setIdentCandidatas] = useState<ComandaAberta[]>([]);
  const [identRequerSelecao, setIdentRequerSelecao] = useState(false);
  const [identSelectedId, setIdentSelectedId] = useState(0);
  const [identLookupError, setIdentLookupError] = useState("");
  const [identEnviarCozinha, setIdentEnviarCozinha] = useState(true);
  const [identChecking, setIdentChecking] = useState(false);
  const [identExisting, setIdentExisting] = useState<ComandaAberta | null>(null);
  const [sending, setSending] = useState(false);
  const [cozinhaConfirmOpen, setCozinhaConfirmOpen] = useState(false);
  const identLookupSeqRef = useRef(0);
  const identAtendimento = buildIdentificacaoGarcom(
    clientConfig.modo_atendimento,
    identNome,
    identNumero,
    identMesa,
  );
  const identNomeNormalizado = normalizeAtendimentoNome(identNome);
  const identNumeroNormalizado = normalizeNumeroGarcom(identNumero, clientConfig.modo_atendimento);
  const identNomeValido = identNome.length <= 10 && (!identNomeNormalizado || isNomeValido(identNomeNormalizado));
  const identValida = identNomeValido && Boolean(identNomeNormalizado || identNumeroNormalizado || (clientConfig.modo_atendimento === "ambos" && normalizeNumeroGarcom(identMesa, "mesa")));
  const tituloAtendimento = (identificacao: string) => {
    const { nome, numero } = parseIdentificacaoGarcom(identificacao, clientConfig.modo_atendimento);
    return clientConfig.modo_atendimento === "ambos" ? identificacao : numero ? formatIdentificacaoGarcomDisplay(clientConfig.modo_atendimento, nome, numero) : identificacao;
  };

  const refreshMesasView = useCallback(
    async (silent: boolean) => {
      mesasRequestRef.current?.abort();
      const controller = new AbortController();
      mesasRequestRef.current = controller;
      if (!silent) setMesasLoading(true);
      try {
        const [list, notifications] = await Promise.all([
          listComandas({ signal: controller.signal }),
          listarGarcomChamados({ signal: controller.signal }).catch(() => null),
        ]);
        if (controller.signal.aborted) return;
        const abertas = new Set(list.map((c) => c.id));
        setComandas(list);
        setChamados((prev) => (notifications ?? prev).filter((c) => {
          const id = notificacaoComandaId(c);
          return id == null || abertas.has(id);
        }));
      } catch (e) {
        if (!silent && !controller.signal.aborted) {
          toast.error(e instanceof Error ? e.message : `Erro ao carregar ${atendimentoLabels.pluralLower}.`);
        }
      } finally {
        if (!controller.signal.aborted) setMesasLoading(false);
      }
    },
    [atendimentoLabels.pluralLower],
  );

  const deveSincronizarMesas = view === "mesas" || editingComandaId != null;
  useEffect(() => {
    if (!deveSincronizarMesas) return;
    let stopped = false;
    let timer: number;
    let pollSequence = 0;
    const poll = async (silent: boolean) => {
      const sequence = ++pollSequence;
      if (document.visibilityState === "visible") await refreshMesasView(silent);
      if (!stopped && sequence === pollSequence) timer = window.setTimeout(() => void poll(true), POLL_MS);
    };
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      window.clearTimeout(timer);
      void poll(true);
    };
    void poll(false);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      mesasRequestRef.current?.abort();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [deveSincronizarMesas, refreshMesasView]);

  const loadCatalogo = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const data = await cardapioApi.catalogo();
      setCatalogo(data);
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao carregar o cardápio.");
      return false;
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedMesa) return;
    if (selectedMesa.comanda && !comandas.some((c) => c.id === selectedMesa.comanda?.id)) {
      setSelectedMesa(null);
      return;
    }
    const key = selectedMesa.key;
    const comanda = comandas.find((c) => garcomComandaRowKey(c, clientConfig.modo_atendimento) === key) ?? null;
    const chamado =
      chamados.find((c): c is GarcomWaiterChamado => c.tipo === "waiter_request" && chamadoMatchesMesaRow(c, selectedMesa, clientConfig.modo_atendimento)) ??
      null;
    const itensNotificacao =
      chamados.find((c): c is GarcomMesaItemsNotification => c.tipo === "mesa_items" && chamadoMatchesMesaRow(c, selectedMesa, clientConfig.modo_atendimento)) ??
      null;
    const contaNotificacao =
      chamados.find((c): c is GarcomCloseAccountNotification => c.tipo === "close_account" && chamadoMatchesMesaRow(c, selectedMesa, clientConfig.modo_atendimento)) ??
      null;
    if (!comanda && !chamado && !itensNotificacao && !contaNotificacao) {
      setSelectedMesa(null);
      return;
    }
    setSelectedMesa((prev) => {
      if (!prev || prev.key !== key) return prev;
      if (
        prev.comanda?.id === comanda?.id &&
        prev.comanda?.total === comanda?.total &&
        prev.comanda?.updatedAt === comanda?.updatedAt &&
        prev.comanda?.nome === comanda?.nome &&
        prev.comanda?.cliente_nome === comanda?.cliente_nome &&
        prev.chamado?.id === chamado?.id &&
        prev.itensNotificacao?.id === itensNotificacao?.id &&
        prev.contaNotificacao?.id === contaNotificacao?.id
      ) {
        return prev;
      }
      return {
        key,
        identificacao: comanda ? garcomComandaDisplay(comanda, clientConfig.modo_atendimento) : prev.identificacao,
        mesaNumero: comanda ? atendimentoNumeroFromNome(comanda.nome, clientConfig.modo_atendimento) : prev.mesaNumero,
        comanda,
        chamado,
        itensNotificacao,
        contaNotificacao,
      };
    });
  }, [comandas, chamados, selectedMesa, clientConfig.modo_atendimento]);

  const mesaCards = useMemo(() => {
    const search = normalizeIdentificacaoSearch(mesaSearch);
    const map = new Map<string, MesaCardRow>();
    const comandaKeyById = new Map<number, string>();

    for (const comanda of comandas) {
      const key = garcomComandaRowKey(comanda, clientConfig.modo_atendimento);
      comandaKeyById.set(comanda.id, key);
      const numero = atendimentoNumeroFromNome(comanda.nome, clientConfig.modo_atendimento);
      const existing = map.get(key);
      const row: MesaCardRow = {
        key,
        identificacao: garcomComandaDisplay(comanda, clientConfig.modo_atendimento),
        mesaNumero: numero,
        comanda,
        chamado: existing?.chamado ?? null,
        itensNotificacao: existing?.itensNotificacao ?? null,
        contaNotificacao: existing?.contaNotificacao ?? null,
      };
      map.set(key, row);
    }

    for (const chamado of chamados) {
      const key = garcomChamadoRowKey(chamado, comandaKeyById, clientConfig.modo_atendimento);
      if (!key) continue;
      const existing = map.get(key);
      if (existing) {
        if (chamado.tipo === "waiter_request") {
          if (!existing.chamado || chamado.id > existing.chamado.id) {
            existing.chamado = chamado;
          }
        } else if (chamado.tipo === "mesa_items") {
          if (!existing.itensNotificacao || chamado.id > existing.itensNotificacao.id) {
            existing.itensNotificacao = chamado;
          }
        } else if (!existing.contaNotificacao || chamado.id > existing.contaNotificacao.id) {
          existing.contaNotificacao = chamado;
        }
      } else if (chamado.tipo === "waiter_request") {
        map.set(key, {
          key,
          identificacao: garcomChamadoDisplay(chamado, clientConfig.modo_atendimento),
          mesaNumero: atendimentoNumeroFromNome(chamado.mesa, clientConfig.modo_atendimento),
          comanda: null,
          chamado,
          itensNotificacao: null,
          contaNotificacao: null,
        });
      } else if (chamado.tipo === "mesa_items") {
        map.set(key, {
          key,
          identificacao: garcomChamadoDisplay(chamado, clientConfig.modo_atendimento),
          mesaNumero: atendimentoNumeroFromNome(chamado.mesa, clientConfig.modo_atendimento),
          comanda: null,
          chamado: null,
          itensNotificacao: chamado,
          contaNotificacao: null,
        });
      } else {
        map.set(key, {
          key,
          identificacao: garcomChamadoDisplay(chamado, clientConfig.modo_atendimento),
          mesaNumero: atendimentoNumeroFromNome(chamado.mesa, clientConfig.modo_atendimento),
          comanda: null,
          chamado: null,
          itensNotificacao: null,
          contaNotificacao: chamado,
        });
      }
    }

    return [...map.values()]
      .filter((row) => {
        if (!search) return true;
        if (/^\d+$/.test(search)) {
          const numero = normalizeNumeroGarcom(search, clientConfig.modo_atendimento);
          if (clientConfig.modo_atendimento === "ambos") {
            const parsed = parseIdentificacaoGarcom(row.identificacao, "ambos");
            const mesa = normalizeNumeroGarcom(search, "mesa");
            return parsed.numero === search || (mesa !== "" && parsed.mesa === mesa);
          }
          return numero !== "" && parseIdentificacaoGarcom(row.identificacao, clientConfig.modo_atendimento).numero === numero;
        }
        return clientConfig.modo_atendimento === "ambos"
          ? normalizeSearchText(row.identificacao).includes(normalizeSearchText(search))
          : normalizeIdentificacaoSearch(row.identificacao).includes(search);
      })
      .sort((a, b) => {
        if (a.mesaNumero != null && b.mesaNumero != null) {
          const compareNumero = clientConfig.modo_atendimento === "mesa"
            ? Number(a.mesaNumero) - Number(b.mesaNumero)
            : a.mesaNumero.localeCompare(b.mesaNumero, "pt-BR");
          return compareNumero || (a.comanda?.id ?? 0) - (b.comanda?.id ?? 0);
        }
        if (a.mesaNumero != null) return -1;
        if (b.mesaNumero != null) return 1;
        return a.identificacao.localeCompare(b.identificacao, "pt-BR") || (a.comanda?.id ?? 0) - (b.comanda?.id ?? 0);
      });
  }, [comandas, chamados, mesaSearch, clientConfig.modo_atendimento]);

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.preco_unitario * item.quantidade, 0),
    [cart],
  );
  const hasEditChanges = useMemo(() => {
    if (editingComandaId == null || editBaselineSignature == null) return false;
    return garcomCartSignature(cart) !== editBaselineSignature;
  }, [editingComandaId, editBaselineSignature, cart]);
  const hasKdsAddition = useMemo(
    () => editingComandaId != null && cart.some((item) => item.origem === "novo"),
    [editingComandaId, cart],
  );
  const hasKdsPendingRemoval = useMemo(() => {
    if (editingComandaId == null) return false;
    const existingKeys = new Set(
      cart.filter((item) => item.origem === "existente").map((item) => item.key),
    );
    return [...editKdsPendingItemKeys].some((key) => !existingKeys.has(key));
  }, [editingComandaId, cart, editKdsPendingItemKeys]);
  const hasKdsRelevantEditChange = hasKdsAddition || hasKdsPendingRemoval;
  const showCartOriginTags = editingComandaId != null && hasEditChanges;
  const canEnviarPedido =
    editingComandaId != null ? hasEditChanges : cart.length > 0;
  const cartOrdered = useMemo(() => {
    if (!showCartOriginTags) return cart;
    return [...cart].sort((a, b) => {
      if (a.origem === b.origem) return 0;
      return a.origem === "novo" ? -1 : 1;
    });
  }, [cart, showCartOriginTags]);

  const filteredCategorias = useMemo(() => {
    if (!catalogo) return [];
    const q = produtoSearch.trim().toLowerCase();
    return catalogo.categorias
      .map((cat) => ({
        ...cat,
        produtos: q
          ? cat.produtos.filter((p) => p.nome.toLowerCase().includes(q))
          : cat.produtos,
      }))
      .filter((cat) => cat.produtos.length > 0);
  }, [catalogo, produtoSearch]);

  const filteredPromos = useMemo(() => {
    if (!catalogo) return [];
    const q = produtoSearch.trim().toLowerCase();
    return catalogo.promocoes.filter((promo) => {
      if (!promo.produto_id) return false;
      if (!q) return true;
      return promo.nome.toLowerCase().includes(q);
    });
  }, [catalogo, produtoSearch]);

  const openCart = useCallback(() => {
    setView("cart");
  }, []);

  const resetPedidoLocal = () => {
    setCozinhaConfirmOpen(false);
    setPedidoIdentificacao(null);
    setEditingComandaId(null);
    setEditBaselineSignature(null);
    setEditKdsPendingItemKeys(new Set());
    setCart([]);
    setProdutoSearch("");
    setModalItem(null);
    setIdentOpen(false);
    setIdentNome("");
    setIdentNumero("");
    setIdentEnviarCozinha(true);
    setIdentChecking(false);
    setIdentExisting(null);
  };

  useEffect(() => {
    if (editingComandaId == null || comandas.some((c) => c.id === editingComandaId)) return;
    resetPedidoLocal();
    setSelectedMesa(null);
    setView("mesas");
    toast.error(`${atendimentoLabels.singular} encerrada ou excluida no Caixa. A edicao foi interrompida.`);
  }, [comandas, editingComandaId, atendimentoLabels.singular]);

  const goMesas = () => {
    resetPedidoLocal();
    setView("mesas");
    void refreshMesasView(true);
  };

  const openMenuForIdentificacao = async (identificacao: string, comandaId: number | null, catalogReady = false) => {
    setPedidoIdentificacao(normalizeIdentificacao(identificacao) || null);
    setEditingComandaId(comandaId);
    setProdutoSearch("");
    setModalItem(null);
    setView("menu");
    if (!catalogo && !catalogReady) {
      const ok = await loadCatalogo();
      if (!ok) {
        resetPedidoLocal();
        setView("mesas");
      }
    }
  };

  const startNovoPedido = async () => {
    setPedidoIdentificacao(null);
    setEditingComandaId(null);
    setEditBaselineSignature(null);
    setEditKdsPendingItemKeys(new Set());
    setCart([]);
    setProdutoSearch("");
    setModalItem(null);
    setIdentOpen(false);
    setIdentNome("");
    setIdentNumero("");
    setIdentEnviarCozinha(true);
    setIdentChecking(false);
    setIdentExisting(null);
    setView("menu");
    if (!catalogo) {
      const ok = await loadCatalogo();
      if (!ok) {
        resetPedidoLocal();
        setView("mesas");
      }
    }
  };

  const openIdentificacao = () => {
    const parsed = parseIdentificacaoGarcom(pedidoIdentificacao, clientConfig.modo_atendimento);
    setIdentNome(parsed.nome);
    setIdentNumero(parsed.numero);
    setIdentMesa(parsed.mesa ?? "");
    setIdentEnviarCozinha(true);
    setIdentExisting(null);
    setIdentOpen(true);
  };

  useEffect(() => {
    if (!identOpen) {
      setIdentChecking(false);
      setIdentExisting(null);
      return;
    }

    const identificacao = identAtendimento;
    const seq = ++identLookupSeqRef.current;
    setIdentSelectedId(0);
    setIdentCandidatas([]);
    setIdentRequerSelecao(false);
    setIdentLookupError("");
    setIdentExisting(null);

    if (!identValida) {
      setIdentChecking(false);
      return;
    }

    setIdentChecking(true);
    const timer = window.setTimeout(() => {
      if (clientConfig.modo_atendimento === "ambos") {
        void lookupComandasPorIdentificacao(identificacao).then((result) => {
          if (identLookupSeqRef.current !== seq) return;
          setIdentExisting(result.comanda);
          setIdentCandidatas(result.candidatas);
          setIdentRequerSelecao(result.requer_selecao);
        }).catch(() => {
          if (identLookupSeqRef.current === seq) setIdentLookupError("Nao foi possivel verificar as comandas. Tente novamente.");
        }).finally(() => {
          if (identLookupSeqRef.current === seq) setIdentChecking(false);
        });
        return;
      }
      void findComandaAbertaPorIdentificacao(identificacao)
        .then((found) => {
          if (identLookupSeqRef.current !== seq) return;
          setIdentExisting(found && found.id !== editingComandaId ? found : null);
        })
        .catch(() => {
          if (identLookupSeqRef.current !== seq) return;
          setIdentExisting(null);
        })
        .finally(() => {
          if (identLookupSeqRef.current === seq) {
            setIdentChecking(false);
          }
        });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [identAtendimento, identValida, identOpen, editingComandaId, clientConfig.modo_atendimento]);

  const openProduct = (produto: CardapioProduto) => {
    if (produto.preco_venda == null) return;
    const available = remainingProductQuantity(cart, produto.id, produto.max_quantidade);
    const min = unidadeVendaPermiteDecimal(produto.unidade_venda) ? 0.01 : 1;
    if (available < min - 0.0001) return;
    setModalItem({
      produto_id: produto.id,
      nome: produto.nome,
      imagem_url: produto.imagem_url,
      preco_unitario: produto.preco_venda,
      max_quantidade: produto.max_quantidade,
      unidade_venda: produto.unidade_venda ?? "unidade",
    });
    setModalQty(Math.min(1, available));
    setModalObs("");
  };

  const openPromo = (promo: CardapioPromocao) => {
    if (!promo.produto_id) {
      toast.error("Promoção indisponível.");
      return;
    }
    const available = remainingProductQuantity(cart, promo.produto_id, promo.max_quantidade);
    const min = unidadeVendaPermiteDecimal(promo.unidade_venda) ? 0.01 : 1;
    if (available < min - 0.0001) return;
    setModalItem({
      produto_id: promo.produto_id,
      nome: promo.nome,
      preco_unitario: promo.valor,
      max_quantidade: promo.max_quantidade,
      unidade_venda: promo.unidade_venda ?? "unidade",
    });
    setModalQty(Math.min(1, available));
    setModalObs("");
  };

  const addToCart = () => {
    if (!modalItem) return;
    const permiteDecimal = unidadeVendaPermiteDecimal(modalItem.unidade_venda);
    const qty = roundQty(modalQty, permiteDecimal);
    if (permiteDecimal ? qty <= 0 : qty < 1) return;
    const obs = normalizeItemObservacao(modalObs);
    if (qty > remainingProductQuantity(cart, modalItem.produto_id, modalItem.max_quantidade) + 0.0001) {
      toast.error("Quantidade acima do estoque disponível.");
      return;
    }
    setCart((prev) => appendNovoItem(prev, {
      ...modalItem,
      key: garcomCartRowKey("novo", modalItem.produto_id, obs),
      descricao: null,
      imagem_url: modalItem.imagem_url ?? null,
      quantidade: qty,
      observacao: obs,
      origem: "novo",
    }, qty));
    setModalItem(null);
  };

  const updateCartQty = (key: string, qty: number) => {
    setCart((prev) => {
      const item = prev.find((row) => row.key === key);
      if (!item) return prev;
      const increase = roundQty(qty - item.quantidade, unidadeVendaPermiteDecimal(item.unidade_venda));
      if (increase > 0) {
        if (increase > remainingProductQuantity(prev, item.produto_id, item.max_quantidade) + 0.0001) return prev;
        if (item.origem === "existente") return appendNovoItem(prev, item, increase);
      }
      return prev
        .map((item) => (item.key === key ? { ...item, quantidade: qty } : item))
        .filter((item) => item.quantidade > 0);
    });
  };

  const removeCartItem = (key: string) => {
    setCart((prev) => prev.filter((item) => item.key !== key));
  };

  const cartPayload = () =>
    cart.map((item) => ({
      cod: item.produto_id,
      nome: item.nome,
      quantidade: item.quantidade,
      precoUnitario: item.preco_unitario,
      observacao: item.observacao || undefined,
    }));

  const persistPedido = async (enviarCozinha: boolean, identificacaoRaw: string) => {
    let identificacao = normalizeIdentificacao(identificacaoRaw);
    if (sending || (editingComandaId == null && cart.length === 0)) return;
    if (editingComandaId == null) {
      const { nome, numero, mesa } = parseIdentificacaoGarcom(identificacao, clientConfig.modo_atendimento);
      if ((!numero && !nome && !mesa) || nome.length > 10 || (nome && !isNomeValido(nome))) {
        openIdentificacao();
        return;
      }
      identificacao = buildIdentificacaoGarcom(clientConfig.modo_atendimento, nome, numero, mesa);
    }
    if (identificacao === "") {
      toast.error(`Informe o nome ou ${atendimentoLabels.numeroLabel.toLowerCase()}.`);
      return;
    }
    setSending(true);
    try {
      if (editingComandaId != null) {
        await updateComanda({
          id: editingComandaId,
          itens: cartPayload(),
          total: cartTotal,
          enviarCozinha,
          notificarGarcom: false,
          identificacaoLivre: true,
        });
        toast.success(enviarCozinha ? "Pedido enviado." : "Pedido enviado (sem envio à cozinha).");
      } else {
        await createComanda({
          nome: identificacao,
          ...(clientConfig.modo_atendimento === "ambos" ? { comandaId: identSelectedId || identExisting?.id } : {}),
          itens: cartPayload(),
          total: cartTotal,
          enviarCozinha,
          notificarGarcom: false,
          identificacaoLivre: true,
        });
        toast.success(enviarCozinha ? "Pedido enviado." : "Pedido enviado (sem envio à cozinha).");
      }
      resetPedidoLocal();
      setView("mesas");
      await refreshMesasView(false);
      void loadCatalogo();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao enviar pedido.");
      if (editingComandaId != null) void refreshMesasView(true);
    } finally {
      setSending(false);
    }
  };
  const handleEnviarPedido = () => {
    if (editingComandaId != null) {
      if (!hasEditChanges) return;
      if (hasKdsRelevantEditChange) {
        setCozinhaConfirmOpen(true);
      } else {
        void persistPedido(false, pedidoIdentificacao ?? "");
      }
      return;
    }
    if (pedidoIdentificacao != null) {
      if (clientConfig.modo_atendimento === "ambos") { openIdentificacao(); return; }
      void persistPedido(identEnviarCozinha, pedidoIdentificacao);
      return;
    }
    openIdentificacao();
  };

  const handleConfirmarChamado = async (chamado: GarcomChamado) => {
    setActionBusy(true);
    try {
      await confirmarGarcomChamado(chamado.id);
      setChamados((prev) => prev.filter((c) => c.id !== chamado.id));
      toast.success(chamado.tipo === "mesa_items" ? "Notificação confirmada." : "Chamado confirmado.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao confirmar chamado.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleConfirmarIdentificacao = async () => {
    if (sending || identChecking || !identValida || identLookupError || (identRequerSelecao && !identSelectedId)) return;
    await persistPedido(identEnviarCozinha, identAtendimento);
  };

  const handleEditarMesa = async (mesa: SelectedMesa) => {
    if (mesa.comanda) {
      await handleEditarComanda(mesa.comanda);
      return;
    }
    setSelectedMesa(null);
    setCart([]);
    setEditBaselineSignature(null);
    setEditKdsPendingItemKeys(new Set());
    await openMenuForIdentificacao(mesa.identificacao, null);
  };

  const handleEditarComanda = async (comanda: ComandaAberta) => {
    const identificacao = garcomComandaDisplay(comanda, clientConfig.modo_atendimento);
    setActionBusy(true);
    try {
      let data = catalogo;
      if (!data) {
        data = await cardapioApi.catalogo();
        setCatalogo(data);
      }
      const detalhe = await getComanda(comanda.id);
      const nextCart: GarcomCartItem[] = [];
      const byKey = new Map<string, GarcomCartItem>();
      const pendingItemKeys = new Set<string>();
      for (const item of detalhe.itens) {
        const obs = normalizeItemObservacao(item.observacao);
        const produto = findCatalogProduct(data, item.produto_id);
        const unidade = produto?.unidade_venda ?? "unidade";
        const maxQtd = Math.max(item.quantidade, produto?.max_quantidade ?? item.quantidade);
        const rowKey = garcomCartRowKey("existente", item.produto_id, obs);
        if (item.cozinha_pendente === true) {
          pendingItemKeys.add(rowKey);
        }
        const existing = byKey.get(rowKey);
        if (existing) {
          existing.quantidade += item.quantidade;
          existing.max_quantidade = Math.max(existing.max_quantidade, maxQtd);
          continue;
        }
        const row: GarcomCartItem = {
          key: rowKey,
          produto_id: item.produto_id,
          nome: item.nome_produto,
          descricao: null,
          imagem_url: produto?.imagem_url ?? null,
          preco_unitario: item.preco_unitario,
          quantidade: item.quantidade,
          observacao: obs,
          max_quantidade: maxQtd,
          unidade_venda: unidade,
          origem: "existente",
        };
        byKey.set(rowKey, row);
        nextCart.push(row);
      }
      setCart(nextCart);
      setEditBaselineSignature(garcomCartSignature(nextCart));
      setEditKdsPendingItemKeys(pendingItemKeys);
      setSelectedMesa(null);
      setPedidoIdentificacao(identificacao);
      setEditingComandaId(comanda.id);
      setProdutoSearch("");
      setModalItem(null);
      setView("cart");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : `Erro ao abrir a ${atendimentoLabels.singularLower}.`);
    } finally {
      setActionBusy(false);
    }
  };

  const mesaLabel = pedidoIdentificacao ? tituloAtendimento(pedidoIdentificacao) : atendimentoLabels.singular;
  if (view === "mesas" && mesasLoading) {
    return (
      <div className="garcom-app">
        <div className="garcom-loading-wrap">
          <PageContentLoader />
        </div>
      </div>
    );
  }

  if (view === "menu" && (catalogLoading || !catalogo)) {
    return (
      <div className="garcom-app">
        <div className="garcom-loading-wrap">
          <PageContentLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="garcom-app">
      {view === "mesas" ? (
        <div className="garcom-shell">
          <h1 className="garcom-title">{atendimentoLabels.plural}</h1>
          <div className="garcom-search">
            <Search className="garcom-search-icon" aria-hidden />
            <input
              type="text"
              autoComplete="off"
              placeholder={`Digite nome ou ${atendimentoLabels.numeroLabel.toLowerCase()}`}
              value={mesaSearch}
              onChange={(e) => setMesaSearch(e.target.value)}
              aria-label={`Pesquisar ${atendimentoLabels.singularLower}`}
            />
          </div>
          <div className="garcom-mesas-grid">
            <button
              type="button"
              className="garcom-mesa-add"
              onClick={() => void startNovoPedido()}
            >
              <span className="garcom-mesa-add-plus">+</span>
              <span className="garcom-mesa-add-label">{atendimentoLabels.singular}</span>
            </button>
            {mesaCards.map((row) => {
              const parsed = parseIdentificacaoGarcom(row.identificacao, clientConfig.modo_atendimento);
              const tileLabel = clientConfig.modo_atendimento === "ambos" ? row.identificacao : parsed.numero || row.identificacao || "—";
              const isNomeOnly = clientConfig.modo_atendimento === "ambos" || (!parsed.numero && tileLabel !== "—");
              return (
                <button
                  key={row.key}
                  type="button"
                  className={`garcom-mesa-card${row.chamado || row.itensNotificacao || row.contaNotificacao ? " garcom-mesa-card--chamado" : ""}`}
                  onClick={() => setSelectedMesa(row)}
                >
                  <span className={`garcom-mesa-num${isNomeOnly ? " garcom-mesa-num--nome" : ""}`}>{tileLabel}</span>
                  {row.comanda ? (
                    <>
                      <span className="garcom-mesa-divider" />
                      <span className="garcom-mesa-total">{formatGarcomBRL(row.comanda.total)}</span>
                    </>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {view === "menu" && catalogo ? (
        <div className="garcom-shell">
          <div className="garcom-header-row">
            <div className="garcom-header-left">
              <button type="button" className="garcom-back" onClick={goMesas} aria-label="Voltar">
                <ChevronLeft size={28} />
              </button>
              <h1 className="garcom-header-title">{mesaLabel}</h1>
            </div>
          </div>
          <div className="garcom-search">
            <Search className="garcom-search-icon" aria-hidden />
            <input
              type="text"
              autoComplete="off"
              placeholder="Digite nome do produto"
              value={produtoSearch}
              onChange={(e) => setProdutoSearch(e.target.value)}
              aria-label="Pesquisar produto"
            />
          </div>
          <div className="garcom-cats">
            {filteredPromos.length > 0 ? (
              <section className="garcom-cat">
                <h2 className="garcom-cat-title">Promoções</h2>
                <div className="garcom-cat-list">
                  {filteredPromos.map((promo) => (
                    <button key={promo.id} type="button" className="garcom-prod" onClick={() => openPromo(promo)}>
                      <span className="garcom-prod-name">{promo.nome}</span>
                      <span className="garcom-prod-side">
                        <span className="garcom-prod-price">{formatGarcomBRL(promo.valor)}</span>
                        <span className="garcom-prod-add" aria-hidden style={{ visibility: promo.produto_id != null && remainingProductQuantity(cart, promo.produto_id, promo.max_quantidade) >= (unidadeVendaPermiteDecimal(promo.unidade_venda) ? 0.01 : 1) - 0.0001 ? "visible" : "hidden" }}>
                          <img src="/imagens/adicionar.svg" alt="" />
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}
            {filteredCategorias.map((cat) => (
              <section key={cat.nome} className="garcom-cat">
                <h2 className="garcom-cat-title">{cat.nome}</h2>
                <div className="garcom-cat-list">
                  {cat.produtos.map((produto) => (
                    <button
                      key={produto.id}
                      type="button"
                      className={`garcom-prod${produto.imagem_url ? " garcom-prod--with-image" : ""}`}
                      onClick={() => openProduct(produto)}
                      disabled={produto.preco_venda == null}
                    >
                      {produto.imagem_url ? <img src={produto.imagem_url} alt="" className="garcom-prod-thumb" /> : null}
                      <span className="garcom-prod-name">{produto.nome}</span>
                      <span className="garcom-prod-side">
                        <span className="garcom-prod-price">{formatGarcomBRL(produto.preco_venda ?? 0)}</span>
                        <span className="garcom-prod-add" aria-hidden style={{ visibility: remainingProductQuantity(cart, produto.id, produto.max_quantidade) >= (unidadeVendaPermiteDecimal(produto.unidade_venda) ? 0.01 : 1) - 0.0001 ? "visible" : "hidden" }}>
                          <img src="/imagens/adicionar.svg" alt="" />
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
          {cart.length > 0 ? (
            <button
              type="button"
              className="garcom-cart-fab"
              onClick={openCart}
              aria-label="Abrir carrinho"
            >
              <ArrowRight size={28} aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}

      {view === "cart" ? (
        <div className="garcom-shell garcom-shell--cart">
          <div className="garcom-header-row">
            <div className="garcom-header-left">
              <button type="button" className="garcom-back" onClick={() => setView("menu")} aria-label="Voltar">
                <ChevronLeft size={28} />
              </button>
              <h1 className="garcom-header-title">{mesaLabel}</h1>
            </div>
            <span className="garcom-header-right">Carrinho</span>
          </div>
          <div className="garcom-cart-scope">
            {cartOrdered.map((item) => {
              const permiteDecimal = unidadeVendaPermiteDecimal(item.unidade_venda);
              return (
                <div key={item.key} className="cardapio-cart-item garcom-cart-item">
                  <div className="garcom-cart-item-main">
                    {showCartOriginTags ? (
                      <span
                        className={`garcom-cart-tag ${
                          item.origem === "existente" ? "garcom-cart-tag--enviado" : "garcom-cart-tag--novo"
                        }`}
                      >
                        {item.origem === "existente" ? "Já pedido" : "Novo"}
                      </span>
                    ) : null}
                    <div className="cardapio-cart-item-info">
                      <p className="cardapio-cart-item-name">{item.nome}</p>
                      {item.observacao ? <p className="cardapio-cart-item-obs">{item.observacao}</p> : null}
                    </div>
                  </div>
                  <div className="cardapio-cart-controls">
                    <ProductQtyControl
                      value={item.quantidade}
                      min={permiteDecimal ? 0.01 : 1}
                      max={item.quantidade + remainingProductQuantity(cart, item.produto_id, item.max_quantidade)}
                      permiteDecimal={permiteDecimal}
                      onChange={(qty) => updateCartQty(item.key, qty)}
                    />
                    <button
                      type="button"
                      className="cardapio-trash"
                      aria-label="Excluir item"
                      onClick={() => removeCartItem(item.key)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
            <div className="cardapio-footer-fixed">
              <div className="cardapio-conta-line">
                <span>Subtotal</span>
                <span>{formatGarcomBRL(cartTotal)}</span>
              </div>
              <div className="cardapio-conta-line total">
                <span>Total:</span>
                <span>{formatGarcomBRL(cartTotal)}</span>
              </div>
              <button
                type="button"
                className="cardapio-btn-primary"
                disabled={!canEnviarPedido || sending}
                onClick={handleEnviarPedido}
              >
                Enviar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {selectedMesa ? (
        <div className="garcom-dialog-scope cardapio-overlay">
          <div
            className="cardapio-modal garcom-mesa-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="garcom-table-title"
          >
            <div className="garcom-mesa-modal-head">
              <h2 id="garcom-table-title" className="cardapio-modal-title">
                {tituloAtendimento(selectedMesa.identificacao)}
              </h2>
              <button
                type="button"
                className="garcom-mesa-modal-close"
                aria-label="Fechar"
                disabled={actionBusy}
                onClick={() => setSelectedMesa(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="garcom-mesa-modal-body">
              {selectedMesa.itensNotificacao ? (
                <>
                  <div className="garcom-chamado-section">
                    <p className="garcom-chamado-label">Novos itens:</p>
                    <ul className="garcom-chamado-list">
                      {getMesaItemsNotificacao(selectedMesa.itensNotificacao.payload).map((item) => (
                        <li key={`${item.produto_id}-${item.nome_produto}-${item.observacao ?? ""}`}>
                          {formatGarcomQuantidadeNotificacao(Number(item.quantidade))}x {item.nome_produto}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    type="button"
                    className="garcom-btn-confirmar-chamado"
                    disabled={actionBusy}
                    onClick={() => void handleConfirmarChamado(selectedMesa.itensNotificacao!)}
                  >
                    Confirmar
                  </button>
                </>
              ) : null}
              {selectedMesa.contaNotificacao ? (
                <>
                  <div className="garcom-chamado-section">
                    <p className="garcom-chamado-label">Fechar conta:</p>
                    <p className="garcom-chamado-obs">
                      {tituloAtendimento(selectedMesa.identificacao)} quer fechar a conta no valor de{" "}
                      {formatGarcomBRL(Number(selectedMesa.contaNotificacao.payload.valor_total || 0))}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="garcom-btn-confirmar-chamado"
                    disabled={actionBusy}
                    onClick={() => void handleConfirmarChamado(selectedMesa.contaNotificacao!)}
                  >
                    Confirmar
                  </button>
                </>
              ) : null}
              {selectedMesa.chamado ? (
                <>
                  <div className="garcom-chamado-section">
                    <p className="garcom-chamado-label">Solicitação:</p>
                    <ul className="garcom-chamado-list">
                      {listWaiterSolicitacoes(selectedMesa.chamado.payload as CardapioWaiterPayload).map(
                        (item) => (
                          <li key={item}>{item}</li>
                        ),
                      )}
                    </ul>
                  </div>
                  {(selectedMesa.chamado.payload as CardapioWaiterPayload).observacao.trim() ? (
                    <div className="garcom-chamado-section">
                      <p className="garcom-chamado-label">Observação:</p>
                      <p className="garcom-chamado-obs">
                        {(selectedMesa.chamado.payload as CardapioWaiterPayload).observacao.trim()}
                      </p>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="garcom-btn-confirmar-chamado"
                    disabled={actionBusy}
                    onClick={() => void handleConfirmarChamado(selectedMesa.chamado!)}
                  >
                    Confirmar
                  </button>
                </>
              ) : !selectedMesa.itensNotificacao && !selectedMesa.contaNotificacao ? (
                <p className="garcom-mesa-modal-empty">Nenhuma notificação.</p>
              ) : null}
            </div>

            <div className="garcom-mesa-modal-foot">
              <button
                type="button"
                className="garcom-btn-editar-mesa"
                disabled={actionBusy}
                onClick={() => void handleEditarMesa(selectedMesa)}
              >
                Editar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {modalItem ? (
        <div className="garcom-product-modal-scope cardapio-overlay">
          <div
            className="cardapio-modal cardapio-modal--product"
            role="dialog"
            aria-modal="true"
            aria-labelledby="garcom-prod-title"
          >
            <h2 id="garcom-prod-title" className="cardapio-modal-title">
              {modalItem.nome}
            </h2>
            <div className="cardapio-qty-row">
              <span style={{ fontWeight: 600 }}>{labelQuantidadeCaixa(modalItem.unidade_venda)}</span>
              <ProductQtyControl
                value={modalQty}
                min={unidadeVendaPermiteDecimal(modalItem.unidade_venda) ? 0.01 : 1}
                max={remainingProductQuantity(cart, modalItem.produto_id, modalItem.max_quantidade)}
                permiteDecimal={unidadeVendaPermiteDecimal(modalItem.unidade_venda)}
                onChange={setModalQty}
              />
            </div>
            <p className="cardapio-obs-label">
              <MessageCircle className="h-4 w-4" /> Quer fazer alguma observação?
            </p>
            <input
              className="cardapio-modal-obs-input"
              placeholder={'Ex: Tirar item "X"'}
              maxLength={OBSERVACAO_ITEM_MAX_LEN}
              value={modalObs}
              onChange={(e) => setModalObs(e.target.value.slice(0, OBSERVACAO_ITEM_MAX_LEN))}
            />
            <div className="cardapio-modal-actions">
              <button type="button" className="cardapio-btn-outline" onClick={() => setModalItem(null)}>
                <ChevronLeft className="h-4 w-4 inline" /> Voltar
              </button>
              <button type="button" className="cardapio-btn-add" onClick={addToCart}>
                <span>Adicionar</span>
                <span>{formatGarcomBRL(modalItem.preco_unitario * modalQty)}</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {cozinhaConfirmOpen ? (
        <div className="garcom-dialog-scope cardapio-overlay">
          <div className="cardapio-modal" role="dialog" aria-modal="true" aria-labelledby="garcom-cozinha-title">
            <h2 id="garcom-cozinha-title" className="cardapio-modal-title">Enviar para a cozinha?</h2>
            <div className="cardapio-modal-footer-actions">
              <button type="button" className="cardapio-btn-outline" disabled={sending} onClick={() => void persistPedido(false, pedidoIdentificacao ?? "")}>Não</button>
              <button type="button" className="cardapio-btn-primary" disabled={sending} onClick={() => void persistPedido(true, pedidoIdentificacao ?? "")}>Sim</button>
            </div>
          </div>
        </div>
      ) : null}

      {identOpen ? (
        <div className="garcom-dialog-scope cardapio-overlay">
          <div className="cardapio-modal" role="dialog" aria-modal="true" aria-labelledby="garcom-ident-title">
            <h2 id="garcom-ident-title" className="cardapio-modal-title">
              {clientConfig.modo_atendimento === "ambos" ? "Comandas" : atendimentoLabels.singular}
            </h2>
            <div className="cardapio-field">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="garcom-ident-nome">Nome</label>
                <span className="text-xs text-zinc-400">{identNome.length}/10</span>
              </div>
              <input
                id="garcom-ident-nome"
                maxLength={10}
                type="text"
                autoComplete="off"
                placeholder="Ex: João"
                value={identNome}
                disabled={sending}
                onChange={(e) => setIdentNome(filterNomeInput(e.target.value).slice(0, 10))}
                onBlur={() => setIdentNome((value) => normalizeAtendimentoNome(value))}
              />
            </div>
            <div className="cardapio-field">
              <label htmlFor="garcom-ident-numero">{atendimentoLabels.numeroLabel}</label>
              <input
                id="garcom-ident-numero"
                maxLength={limiteNumeroGarcom(clientConfig.modo_atendimento)}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                placeholder="Ex: 5"
                value={identNumero}
                disabled={sending}
                onChange={(e) => setIdentNumero(filterNumeroGarcom(e.target.value, clientConfig.modo_atendimento))}
                onBlur={() => setIdentNumero((value) => normalizeNumeroGarcom(value, clientConfig.modo_atendimento))}
              />
            </div>
            {clientConfig.modo_atendimento === "ambos" ? <>
              <div className="cardapio-field">
                <label htmlFor="garcom-ident-mesa">Nº Mesa</label>
                <input id="garcom-ident-mesa" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={3} value={identMesa} disabled={sending}
                  onChange={(e) => setIdentMesa(filterNumeroGarcom(e.target.value, "mesa"))}
                  onBlur={() => setIdentMesa((value) => normalizeNumeroGarcom(value, "mesa"))} />
              </div>
              {!identValida ? <p role="status">Preencha pelo menos Nome, Nº Comanda ou Nº Mesa.</p> : null}
              {identLookupError ? <p role="alert">{identLookupError}</p> : null}
              {identRequerSelecao ? <div className="cardapio-field">
                <label htmlFor="garcom-ident-selecao">Selecione a comanda correta</label>
                <select id="garcom-ident-selecao" value={identSelectedId} onChange={(e) => setIdentSelectedId(Number(e.target.value))} disabled={sending}>
                  <option value={0}>Selecione...</option>
                  {identCandidatas.map((c) => <option key={c.id} value={c.id}>{c.nome} (#{c.id})</option>)}
                </select>
              </div> : null}
            </> : null}
            {identChecking ? (
              <div className="mt-2 flex items-center gap-2 text-sm text-zinc-300">
                <Loader2 className="h-4 w-4 animate-spin" />
                Verificando identificação...
              </div>
            ) : null}
            {identExisting ? (
              <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
                {atendimentoLabels.jaExiste} Os itens serão adicionados a ela.
              </div>
            ) : null}
            <label className="garcom-ident-check" htmlFor="garcom-ident-cozinha">
              <input
                id="garcom-ident-cozinha"
                type="checkbox"
                className="cardapio-checkbox"
                checked={identEnviarCozinha}
                disabled={sending}
                onChange={(e) => setIdentEnviarCozinha(e.target.checked)}
              />
              Enviar para cozinha
            </label>
            <div className="cardapio-modal-footer-actions">
              <button
                type="button"
                className="cardapio-btn-outline"
                disabled={sending}
                onClick={() => setIdentOpen(false)}
              >
                <ChevronLeft className="h-4 w-4 inline" /> Voltar
              </button>
              <button
                type="button"
                className="cardapio-btn-primary"
                disabled={sending || identChecking || !identValida || Boolean(identLookupError) || (identRequerSelecao && !identSelectedId)}
                onClick={() => void handleConfirmarIdentificacao()}
              >
                {identExisting ? atendimentoLabels.adicionar : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
